import { TBillingInterval } from "@/types/app";
import { TProductRefId } from "@/types/plan";
import { sumIntervalPrices } from "@/utils/billingInterval";
import { formatPriceForBilling } from "@/utils/formatPriceForBilling";
import { BUNDLE_DISCOUNT_RATE } from "../constants.checkout";
import { TPlan } from "../types.checkout";

/**
 * How much of the bundle is ticked for cancellation. Drives the whole cancel
 * dialog — the summary layout, which notices appear, and the copy in each.
 *
 * - `none`    — nothing ticked; the bundle is intact and only today's price matters
 * - `partial` — some plans ticked; the bundle breaks and the rest reprice
 * - `all`     — everything ticked; there is no plan left, the subscription ends
 */
export type TCancelState = "none" | "partial" | "all";

/**
 * Prices are dollars carrying float error from the discount multiply, so treat
 * anything under half a cent as "the same price" rather than a real change.
 */
const PRICE_EPSILON = 0.005;

export type TCancelSummary = {
  /** Plans left over after the cancellation */
  keptPlans: TPlan[];
  /** Every plan at full individual price, before the bundle discount */
  fullTotal: number | undefined;
  /** What the user pays today — the Complete card's price straight from the API */
  currentTotal: number | undefined;
  /** What the kept plans cost once the bundle breaks and the discount is lost */
  afterTotal: number | undefined;
  /** Change from `currentTotal` to `afterTotal` — positive means paying more */
  delta: number | undefined;
  /** How much of the bundle is ticked — see {@link TCancelState} */
  state: TCancelState;
  /** Kept plans cost less than today's bundle, so credits need adjusting */
  isCheaperAfter: boolean;
  /** Losing the discount outweighs the dropped plan — fewer plans, bigger bill */
  isPricierAfter: boolean;
};

/** How much of `plans` is ticked for cancellation. */
export function getCancelState(
  plans: TPlan[],
  selectedIds: TProductRefId[],
): TCancelState {
  if (selectedIds.length === 0) return "none";
  // Compared against the plans on screen, not the raw count, so an id that
  // isn't in the list can't make a partial selection read as a full one.
  const selectedShown = plans.filter((plan) =>
    selectedIds.includes(plan.productRefId),
  );
  return selectedShown.length >= plans.length ? "all" : "partial";
}

/**
 * Works out the price impact of cancelling `selectedIds` out of a bundle.
 *
 * Cancelling any part of the bundle breaks it, so the plans that remain fall
 * back to full individual pricing — which can cost more than the discounted
 * bundle even though there are fewer plans.
 *
 * @param bundlePlan The Complete plan card from the checkout catalog; its price
 * is today's bill. Omit it and the bundle discount is applied locally instead.
 */
export function getCancelSummary(
  plans: TPlan[],
  selectedIds: TProductRefId[],
  billingPeriod: TBillingInterval,
  bundlePlan?: TPlan,
): TCancelSummary {
  const priceOf = (plan: TPlan) =>
    formatPriceForBilling(plan.pricing, billingPeriod);

  const sumOf = (list: TPlan[]) => sumIntervalPrices(list.map(priceOf));

  const keptPlans = plans.filter(
    (plan) => !selectedIds.includes(plan.productRefId),
  );

  const state = getCancelState(plans, selectedIds);

  // Strike-through price: the bundle's catalog card only carries the bundle
  // price, so "before the discount" is still the individual plans added up —
  // same treatment the pricing page gives it (see `calculateIndividualTotals`).
  const fullTotal = sumOf(plans);

  // What the bundle actually bills is priced server-side, so take it from the
  // Complete card `/pricing/metadata` returns. The local discount is only a
  // fallback for when that card is missing from the catalog.
  const currentTotal = bundlePlan
    ? priceOf(bundlePlan)
    : fullTotal === undefined
      ? undefined
      : fullTotal * (1 - BUNDLE_DISCOUNT_RATE);

  const afterTotal = state === "none" ? currentTotal : sumOf(keptPlans);

  // Unpriced for this cycle — no totals to compare, so no verdict either.
  if (currentTotal === undefined || afterTotal === undefined) {
    return {
      keptPlans,
      fullTotal,
      currentTotal,
      afterTotal,
      delta: undefined,
      state,
      isCheaperAfter: false,
      isPricierAfter: false,
    };
  }

  return {
    keptPlans,
    fullTotal,
    currentTotal,
    afterTotal,
    delta: afterTotal - currentTotal,
    state,
    isCheaperAfter: afterTotal < currentTotal - PRICE_EPSILON,
    isPricierAfter: afterTotal > currentTotal + PRICE_EPSILON,
  };
}
